import { useState } from 'react';
import clsx from 'clsx';
import { PolicyChip } from './PolicyChip';
import { useFindContractDocument } from '../../../application';
import { FraudAlert } from '../../../domain/entities';
import { Policy } from '../../../domain/entities/Policy';
import { PolicyStatus } from '../../../domain/values';
import { CalendarIcon, Card, Chip, ChipType, RefreshIcon } from '../../../ui/atoms';
import { stylesMode } from '../../../ui/styles';
import PolicyCardPolicyIdentifierDetails from '../../molecules/policy-card/PolicyCardPolicyIdentifierDetails';
import { getFlagEmoji } from '../../utils/index';
import { useModuleTranslation } from '../../utils/useModuleTranslation';
import { getDialogDetailsFromError } from '../dialog/dialogUtils';
import useDialog from '../dialog/useDialog';
import FraudAlertTooltip from '../fraud-alert-tooltip/FraudAlertTooltip';
import PetPoliciesModal from '../pet-policies-modal/PetPoliciesModal';
import './policy-card.modules.scss';

// The Barkibu-backed cue means exactly one thing on this card: a prevention policy still
// on a legacy, non-CRL product version. Correlation-backed prevention (CRL_...) and every
// other product keep the default accent. Both the primary version marker and the
// subordinate also-available tags read the rule from here so the two cannot drift apart.
const isBarkibuBackedPrevention = (productType: string | undefined, productVersionTag: string | undefined): boolean =>
    Boolean(productType?.toLowerCase().includes('prevention')) &&
    productVersionTag != null &&
    !productVersionTag.includes('CRL');

// General Liability (sold as Pet Liability) never informs a health or prevention review, so its
// version tags stay out of the subordinate also-available line. PAS is the source of truth for
// what GL is and keys it off the product id prefix — Product#gl? is `id.match?(/^gl_/)` — which in
// production covers exactly gl_basic, gl_extended and gl_correlation_{es,it,pt}. The version tag is
// deliberately not the signal: the German TIERdirekt legacy tariff `NV Haftpflicht Hund d0` reads as
// liability by name yet PAS classifies it as health, so a name match would hide a health product.
const isGeneralLiability = (productType: string | undefined): boolean =>
    Boolean(productType?.toLowerCase().startsWith('gl_'));

type AlsoAvailableProductVersion = { productVersionTag: string; barkibuBacked: boolean };

interface PolicyCardProps {
    policyIdentifier: Policy['identifier'];
    fraudAlerts?: FraudAlert[];
    countryCode?: string;
    setLoading: (loading: boolean) => void;
    tags?: string[];
    petName: string;
    policies: Policy[];
    onPolicyClick?: (policy: Policy) => void;
    contractIdsActiveOnClaimRequest: string[];
    invoiceDates?: Date[];
    totalInvoiceCount?: number;
}

export default function PolicyCard({
    policyIdentifier,
    setLoading,
    fraudAlerts,
    countryCode,
    tags,
    petName,
    policies,
    onPolicyClick,
    contractIdsActiveOnClaimRequest,
    invoiceDates,
    totalInvoiceCount,
}: PolicyCardProps) {
    const { t } = useModuleTranslation();
    const unknownValue = t('value.unknown', { ns: 'commons' });
    const { openDialog } = useDialog();
    const { viewContractDocument, downloadContractDocument } = useFindContractDocument();

    const [showPoliciesModal, setShowPoliciesModal] = useState(false);

    const policy = policies.find((policy) => policy.identifier === policyIdentifier)!;

    const handleViewDocumentClick = async () => {
        try {
            setLoading(true);
            viewContractDocument(policyIdentifier).then(() => setLoading(false));
        } catch (e) {
            setLoading(false);
            openDialog(t('document.visualisation_error'), getDialogDetailsFromError(e));
        }
    };

    const handleDownloadDocumentClick = async () => {
        try {
            setLoading(true);
            downloadContractDocument(policyIdentifier).then(() => setLoading(false));
        } catch (e) {
            setLoading(false);
            openDialog(t('document.download_error'), getDialogDetailsFromError(e));
        }
    };

    const policyTitle = policy.productType
        ? getFlagEmoji(countryCode) +
          ' ' +
          t(`policy.${policy.productType.replace(/(.*)_\w{2}$/, '$1').toLowerCase()}.name`, policy.productType)
        : t(`policy.not_found`);

    const policyItemStyle = stylesMode({ with: 'policy', prefix: 'policy', modes: ['description-item'] });

    const startDateFormatted = policy.startDate ? Intl.DateTimeFormat('es').format(policy.startDate) : undefined;
    const expirationDate = policy.expirationDate;
    const expirationDateFormatted = expirationDate ? Intl.DateTimeFormat('es').format(expirationDate) : undefined;
    const coveredUntilDate = policy.coveredUntil;
    const coveredUntilDateFormatted = coveredUntilDate ? Intl.DateTimeFormat('es').format(coveredUntilDate) : undefined;
    const showCoveredUntilExtension =
        coveredUntilDate != null &&
        expirationDate != null &&
        coveredUntilDate.getTime() !== expirationDate.getTime();

    const effectiveStartDateFormatted = policy.effectiveStartDate
        ? Intl.DateTimeFormat('es').format(policy.effectiveStartDate)
        : undefined;

    const details = [
        {
            icon: CalendarIcon,
            text: startDateFormatted
                ? t('policy.started_on', { startDate: effectiveStartDateFormatted })
                : unknownValue,
        },
    ];

    if (startDateFormatted !== effectiveStartDateFormatted) {
        details.push({
            icon: CalendarIcon,
            text: effectiveStartDateFormatted ? t('policy.active_on', { startDate: startDateFormatted }) : unknownValue,
        });
    }

    details.push({
        icon: RefreshIcon,
        text: expirationDateFormatted
            ? showCoveredUntilExtension
                ? t('policy.expires_on_with_covered_until', {
                      expirationDate: expirationDateFormatted,
                      coveredUntil: coveredUntilDateFormatted,
                  })
                : t('policy.expires_on', { expirationDate: expirationDateFormatted })
            : unknownValue,
    });

    const coverageWarning = (() => {
        if (!invoiceDates || invoiceDates.length === 0 || !coveredUntilDate) return undefined;

        const hasDateOutsideCoverage = invoiceDates.some((invoiceDate) => invoiceDate > coveredUntilDate);
        if (!hasDateOutsideCoverage) return undefined;

        return (totalInvoiceCount ?? invoiceDates.length) === 1
            ? t('policy.invoice_date_outside_coverage')
            : t('policy.some_invoice_dates_outside_coverage');
    })();

    const fraudAlert = fraudAlerts && fraudAlerts.length > 0 ? <FraudAlertTooltip /> : undefined;
    const contractsApplying = policy?.getActiveContracts(contractIdsActiveOnClaimRequest);
    const activeProductVersionTags = contractsApplying
        ?.map((contract) => contract?.productVersionTag)
        .filter((productVersionTag): productVersionTag is string => Boolean(productVersionTag));
    const currentProductVersionTag = activeProductVersionTags?.[0];
    const isLegacyBarkibuBackedPrevention = isBarkibuBackedPrevention(policy.productType, currentProductVersionTag);
    const policyInfo = activeProductVersionTags ? (
        <>
            {activeProductVersionTags.map((productVersionTag, index) =>
                productVersionTag ? (
                    <span
                        key={index}
                        className={clsx(
                            isLegacyBarkibuBackedPrevention && productVersionTag === currentProductVersionTag &&
                                'policy-info-tag--barkibu-backed'
                        )}
                    >
                        {productVersionTag}
                    </span>
                ) : undefined
            )}
        </>
    ) : undefined;
    const alsoAvailableProductVersions = ((): AlsoAvailableProductVersion[] => {
        const byProductVersionTag = new Map<string, AlsoAvailableProductVersion>();

        policies
            .filter((otherPolicy) => otherPolicy.identifier !== policyIdentifier)
            .filter((otherPolicy) => !isGeneralLiability(otherPolicy.productType))
            .forEach((otherPolicy) =>
                otherPolicy.getContractsCoveringAnyOf(invoiceDates ?? []).forEach(({ productVersionTag }) => {
                    if (!productVersionTag) return;

                    byProductVersionTag.set(productVersionTag, {
                        productVersionTag,
                        barkibuBacked: isBarkibuBackedPrevention(otherPolicy.productType, productVersionTag),
                    });
                })
            );

        return Array.from(byProductVersionTag.values());
    })();
    const alsoAvailableInfo =
        alsoAvailableProductVersions.length > 0 ? (
            <div className="policy-info-also-available">
                <span className="policy-info-also-available-label">{t('policy.also_available')}</span>
                {alsoAvailableProductVersions.map(({ productVersionTag, barkibuBacked }) => (
                    <span
                        key={productVersionTag}
                        className={clsx(
                            'policy-info-also-available-tag',
                            barkibuBacked && 'policy-info-also-available-tag--barkibu-backed'
                        )}
                    >
                        {productVersionTag}
                    </span>
                ))}
            </div>
        ) : undefined;
    const showNonRenewalChip =
        policy.renewalStopped && ![PolicyStatus.CANCELLED, PolicyStatus.CLOSED].includes(policy.status);
    const nonRenewalChip = showNonRenewalChip ? (
        <Chip
            type={ChipType.NEUTRAL}
            text={t('policy.non_renewal_requested')}
            accessibilityText={t('policy.non_renewal_requested')}
        />
    ) : undefined;
    const policyStatusChip = policy.status ? (
        <section>
            <PolicyChip status={policy.status}></PolicyChip>{' '}
            {nonRenewalChip}{' '}
            {tags && tags.length > 0 && (
                <div className="policy-tags">
                    {tags.map((tag, index) => (
                        <Chip type={ChipType.NEUTRAL} text={tag} accessibilityText={tag} key={index} />
                    ))}
                </div>
            )}
        </section>
    ) : undefined;
    const policyTitleComponent = (
        <div className="policy-card-title">
            <span>{policyTitle}</span>
            {fraudAlert}
        </div>
    );

    // The also-available line is a sibling of the status/version row, not a third item inside
    // the narrow version column: on its own full-width line a long tag such as
    // CRL_Prevention_ES_2026_W1.0_P9.1 stays whole instead of wrapping under its own label.
    const policySubheader = (
        <div className="policy-card-sub-header">
            <div className="policy-card-sub-header-row">
                {policyStatusChip}
                <div className="policy-info-container">{policyInfo}</div>
            </div>
            {alsoAvailableInfo}
        </div>
    );

    const handlePolicyClick = () => {
        return onPolicyClick != null
            ? (policy: Policy) => {
                  onPolicyClick(policy);
                  setShowPoliciesModal(false);
              }
            : undefined;
    };

    return (
        <Card
            title={policyTitleComponent}
            className={clsx('card--policy', policy.status == null && 'card--policy__not-found')}
            subheader={policySubheader}
        >
            <PolicyCardPolicyIdentifierDetails
                onViewDocument={handleViewDocumentClick}
                onDownloadDocument={handleDownloadDocumentClick}
                onShowPoliciesClick={() => setShowPoliciesModal(true)}
                policyIdentifier={policyIdentifier}
            />
            <div className="policy-details">
                {details.map((details, i) => (
                    <div key={i} className={policyItemStyle}>
                        <details.icon className="icon" />
                        {details.text}
                    </div>
                ))}
            </div>
            {coverageWarning && <Chip type={ChipType.WARNING} text={coverageWarning} accessibilityText={coverageWarning} />}
            {showPoliciesModal && (
                <PetPoliciesModal
                    name={petName}
                    policies={policies}
                    onClose={() => setShowPoliciesModal(false)}
                    onPolicyClick={handlePolicyClick()}
                />
            )}
        </Card>
    );
}
